import { searchIndex, type SearchEntry } from "../data/searchIndex";

export interface SearchResult {
    entry: SearchEntry;
    title: string;
    snippet: string;
    score: number;
}

/** An index entry with its i18n keys already resolved to text. */
export interface ResolvedEntry {
    entry: SearchEntry;
    title: string;
    bodies: string[];
    /** Synonyms, kept apart from `bodies` so they never surface in a snippet. */
    aliases: string;
    /**
     * The same entry in English, matched but never displayed. Visitors mix
     * languages — someone reading the Greek site may well type "menu" rather
     * than "μενού" — so English is always searchable alongside the locale in
     * front of them. Empty when English *is* the current locale, so a hit is
     * never counted twice.
     */
    fallbackTitle: string;
    fallbackBody: string;
    /** The English synonym list, scored as an alias rather than as prose. */
    fallbackAliases: string;
}

const COMBINING_MARKS = /\p{M}/gu;
/**
 * Hyphens, and only hyphens — not the en and em dashes the copy uses as
 * punctuation, which sit between spaces and so already separate their words.
 */
const HYPHENS = /[-\u2010\u2011]/g;

/**
 * Folds text down to something comparable across the five locales.
 *
 * Greek is the reason this exists: without decomposing accents a search for
 * "καταρρακτης" misses "Καταρράκτης", and without folding the final sigma
 * "ς" the same word fails to match itself depending on where it sits in a
 * sentence. NFD splits the accent into a combining mark that gets stripped;
 * `toLowerCase()` already maps a trailing "Σ" to "ς", so the fold runs last.
 *
 * Hyphens go too, on both sides, because nobody types them the way the copy
 * spells them: the FAQ says "Wi-Fi", "check-in" and "gluten-free", and
 * visitors type "wifi", "checkin", "gluten free". Apostrophes deliberately
 * stay — they are a word boundary in French and Italian ("l'eau", "dell'acqua"),
 * and removing them would bury a search for "eau" mid-word.
 */
export function normalizeForSearch(value: string): string {
    return value
        .normalize("NFD")
        .replace(COMBINING_MARKS, "")
        .toLowerCase()
        .replace(/ς/g, "σ")
        .replace(HYPHENS, "");
}

/** Everything reaching the matcher has to be a string; anything else is no use for matching. */
function asText(value: unknown): string {
    return typeof value === "string" ? value : "";
}

/** Weights: a hit in the heading is worth far more than one buried in prose. */
const SCORE_TITLE_PREFIX = 12;
const SCORE_TITLE = 8;
const SCORE_BODY = 3;
/** A curated synonym is a deliberate signal, so it outranks an incidental mention in prose. */
const SCORE_ALIAS = 5;
/**
 * English matches rank below their equivalents in the language being read: the
 * locale in front of the visitor is the better answer when both hit. A title
 * still beats prose across languages — typing "menu" should reach the page
 * *called* Menu before one that merely mentions μενού in passing.
 */
const SCORE_FALLBACK_TITLE = 4;
const SCORE_FALLBACK_ALIAS = 3;
const SCORE_FALLBACK_BODY = 1;
/** Mid-word hits still count, but only just — see matchKind(). */
const SCORE_TITLE_INNER = 2;
const SCORE_BODY_INNER = 1;
/**
 * A mid-word hit in the English title sits below a mid-word hit in the native
 * title (2): otherwise "chen" reaches Kitchen on the German site ahead of a
 * German page that actually contains it.
 */
const SCORE_FALLBACK_TITLE_INNER = 1;
/**
 * A title that is a sentence rather than a label — see `proseTitle` in
 * `data/searchIndex.ts`. Deliberately between prose (3) and a curated synonym
 * (5): "crampons" in an FAQ question is a better answer than the same word
 * mentioned in passing on the winter school page, while "room" still belongs to
 * whichever entry lists it as a synonym rather than to "Is there laundry or a
 * drying room?".
 */
const SCORE_PROSE_TITLE = 4;

/**
 * Below this length a mid-word hit is almost always noise rather than a stem:
 * "ice" inside "services", "art" inside "Sparta".
 */
const MIN_INNER_MATCH_LENGTH = 4;

const WORD_CHARACTER = /[\p{L}\p{N}]/u;

type MatchKind = "start" | "boundary" | "inner" | null;

/**
 * Where a token sits inside a string. Matching plain substrings is what makes
 * a search for "ice" return "Services" and "Soft Drinks", so a hit that starts
 * at a word boundary is ranked well above one buried mid-word — and for short
 * tokens a mid-word hit is discarded outright.
 *
 * Word characters are decided by Unicode property, not \w, which would treat
 * every Greek letter as a boundary.
 */
function matchKind(haystack: string, token: string): MatchKind {
    let hasInner = false;

    for (let at = haystack.indexOf(token); at !== -1; at = haystack.indexOf(token, at + 1)) {
        if (at === 0) return "start";
        if (!WORD_CHARACTER.test(haystack[at - 1])) return "boundary";
        hasInner = true;
    }

    if (!hasInner) return null;
    return token.length >= MIN_INNER_MATCH_LENGTH ? "inner" : null;
}

const MATCH_RANK: Record<Exclude<MatchKind, null>, number> = { start: 3, boundary: 2, inner: 1 };

/**
 * The best kind across several strings. `matchKind()` already returns the best
 * kind within one string; this does the same across paragraphs, so that a weak
 * hit in an early paragraph cannot hide a strong one further down.
 */
function bestMatchKind(haystacks: string[], token: string): MatchKind {
    let best: MatchKind = null;

    for (const haystack of haystacks) {
        const kind = matchKind(haystack, token);
        if (kind === "start") return kind;
        if (kind && (!best || MATCH_RANK[kind] > MATCH_RANK[best])) best = kind;
    }

    return best;
}

const MAX_RESULTS = 8;
const SNIPPET_RADIUS = 55;

/**
 * Pulls the matched phrase out of the body text so a result shows *why* it
 * matched, not just the section heading.
 */
function buildSnippet(bodies: string[], normalizedBodies: string[], token: string): string {
    for (let i = 0; i < bodies.length; i++) {
        const at = normalizedBodies[i].indexOf(token);
        if (at === -1) continue;

        const body = bodies[i];
        const start = Math.max(0, at - SNIPPET_RADIUS);
        const end = Math.min(body.length, at + token.length + SNIPPET_RADIUS);

        return (start > 0 ? "\u2026" : "") + body.slice(start, end).trim() + (end < body.length ? "\u2026" : "");
    }

    return bodies[0] ?? "";
}

/**
 * Ranks resolved entries against a raw query string. Kept free of Vue and
 * i18n so it can be exercised directly against the locale files.
 */
export function rankSearchResults(entries: ResolvedEntry[], rawQuery: string, limit = MAX_RESULTS): SearchResult[] {
    const tokens = normalizeForSearch(rawQuery).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];

    const matched: SearchResult[] = [];

    for (const candidate of entries) {
        const normalizedTitle = normalizeForSearch(candidate.title);
        const normalizedBodies = candidate.bodies.map(normalizeForSearch);
        const normalizedAliases = normalizeForSearch(candidate.aliases);
        const normalizedFallbackTitle = normalizeForSearch(candidate.fallbackTitle);
        const normalizedFallbackBody = normalizeForSearch(candidate.fallbackBody);
        const normalizedFallbackAliases = normalizeForSearch(candidate.fallbackAliases);
        // A sentence-length title is a weaker signal than a label: "Is there
        // laundry or a drying room?" mentions a room the way prose does, and
        // must not outrank the curated synonym that points "room" at the
        // sleeping arrangements. Scored as body text on both sides.
        const prose = candidate.entry.proseTitle === true;
        let score = 0;

        // Every token has to land somewhere in this entry: typing
        // "winter school" should not return everything about winter.
        const allTokensHit = tokens.every(token => {
            const inTitle = matchKind(normalizedTitle, token);
            const titleScore = !inTitle ? 0
                : prose ? (inTitle === "inner" ? SCORE_BODY_INNER : SCORE_PROSE_TITLE)
                : inTitle === "start" ? SCORE_TITLE_PREFIX
                : inTitle === "boundary" ? SCORE_TITLE
                : SCORE_TITLE_INNER;

            // Body and aliases are both consulted and the better of the two
            // wins: an entry that mentions the word *and* lists it as a synonym
            // should not be pushed below one that only mentions it in passing.
            // The best kind across all body strings, not the first string with
            // any hit: an earlier mid-word occurrence ("ημέρες") must not mask
            // a later whole-word one ("20 μέρες") in another paragraph.
            const inBody = bestMatchKind(normalizedBodies, token);
            const inAliases = matchKind(normalizedAliases, token);

            const bodyScore = !inBody ? 0 : inBody === "inner" ? SCORE_BODY_INNER : SCORE_BODY;
            const aliasScore = !inAliases ? 0 : inAliases === "inner" ? SCORE_BODY_INNER : SCORE_ALIAS;

            // Then the same entry in English, scored lower so it only decides
            // the ranking when the current locale did not match at all.
            const inFallbackTitle = matchKind(normalizedFallbackTitle, token);
            const inFallbackAliases = matchKind(normalizedFallbackAliases, token);
            const inFallbackBody = matchKind(normalizedFallbackBody, token);
            const fallbackScore = Math.max(
                !inFallbackTitle ? 0
                    : prose ? SCORE_FALLBACK_BODY
                    : inFallbackTitle === "inner" ? SCORE_FALLBACK_TITLE_INNER : SCORE_FALLBACK_TITLE,
                !inFallbackAliases ? 0 : inFallbackAliases === "inner" ? SCORE_FALLBACK_BODY : SCORE_FALLBACK_ALIAS,
                !inFallbackBody ? 0 : SCORE_FALLBACK_BODY,
            );

            const best = Math.max(titleScore, bodyScore, aliasScore, fallbackScore);
            if (best === 0) return false;

            score += best;
            return true;
        });

        if (!allTokensHit) continue;

        matched.push({
            entry: candidate.entry,
            title: candidate.title,
            snippet: buildSnippet(candidate.bodies, normalizedBodies, tokens[0]),
            score,
        });
    }

    // Ties break on the order entries appear in data/searchIndex.ts, which sort
    // stability preserves. That order is editorial — home, refuge, booking,
    // services, activities — so "price" leads with the overnight cost and then
    // offers the menu. Sorting tied titles alphabetically instead made the
    // leading result depend on the language: "Our Menu" sorts above "Overnight
    // Stay Cost" in English while Κόστος Ύπνου sorts above Το μενού μας in
    // Greek, so the same query answered differently per locale.
    return matched
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);
}

export function useSiteSearch() {
    const { t, te, locale } = useI18n();
    const localePath = useLocalePath();

    const query = ref("");

    // The haystack is translated text, so it has to be rebuilt whenever the
    // locale changes. `t` is a plain function and not a reactive dependency on
    // its own, hence the explicit read of `locale.value`.
    const resolved = computed<ResolvedEntry[]>(() => {
        void locale.value;

        // Do not import i18n/locales/en.json for this: @nuxtjs/i18n precompiles
        // locale messages, so a direct import hands back compiled message
        // functions rather than text. Ask vue-i18n for the English rendering.
        //
        // te() rather than comparing the result against the key: about 33
        // legacy keys are Title Case English ("Booking", "Camping", "Photos"),
        // so their English value *is* the key name, and treating that as a
        // missing translation would drop the fallback on exactly those entries.
        const needsFallback = locale.value !== "en";
        const inEnglish = (key: string) =>
            needsFallback && te(key, "en") ? asText(t(key, {}, { locale: "en" })) : "";

        return searchIndex.map(entry => ({
            entry,
            title: t(entry.titleKey),
            bodies: entry.bodyKeys.map(key => t(key)),
            aliases: entry.aliasKey ? t(entry.aliasKey) : "",
            fallbackTitle: inEnglish(entry.titleKey),
            fallbackBody: entry.bodyKeys.map(inEnglish).join(" "),
            // Kept apart from fallbackBody so English synonyms score as aliases
            // rather than as prose — "how much" should reach the pricing section
            // from the Greek site nearly as strongly as it does from the English one.
            fallbackAliases: entry.aliasKey ? inEnglish(entry.aliasKey) : "",
        }));
    });

    const results = computed(() => rankSearchResults(resolved.value, query.value));

    /**
     * `localePath()` silently drops "#hash" from a plain string, so anything
     * pointing at a section has to go through the route-object form. Same
     * workaround as components/Buttons/InternalRedirectButton.vue.
     */
    function resolve(entry: SearchEntry) {
        return entry.hash
            ? localePath({ path: entry.path, hash: `#${entry.hash}` })
            : localePath(entry.path);
    }

    return { query, results, resolve };
}
